/**
 * Nav Tree Builders — Declarative tree DSL for sidebar navigation.
 *
 * 트리를 "있는 그대로" 선언하고, flatten()이 기존 flat 배열과 동일한 결과를 자동 생성.
 *
 *   domain("검색키워드", [
 *     group({ ... }, [
 *       leaf({ ... }),
 *     ]),
 *     tool({ ... }),
 *   ]),
 */
import type { LucideIcon } from "lucide-react";

/* ─── Shared option types ─── */

export type ToolCategory = "smart" | "data" | "automation";

export interface AssistantOpts {
  assistantIntents?: string[];
  assistantPrompt?: string;
}

export interface BaseOpts extends AssistantOpts {
  category?: ToolCategory;
  ai?: boolean;
}

/* ─── Node types (discriminated union) ─── */

export interface DomainNode {
  kind: "domain";
  key: string;
  children: Array<GroupNode | ToolNode>;
}

export interface GroupNode {
  kind: "group";
  label: string;
  desc: string;
  to: string;
  Icon: LucideIcon;
  opts: BaseOpts;
  children: LeafNode[];
}

export interface ToolNode {
  kind: "tool";
  label: string;
  desc: string;
  to: string;
  Icon: LucideIcon;
  opts: BaseOpts;
}

export interface LeafNode {
  kind: "leaf";
  navLabel: string;
  label: string;
  desc: string;
  to: string;
  Icon: LucideIcon;
  opts: BaseOpts;
}

export type NavTreeNode = DomainNode | GroupNode | ToolNode | LeafNode;

/* ─── Builder functions ─── */

/** L1: Domain section (사이드바 접기 가능 그룹) */
export function domain(
  key: string,
  children: Array<GroupNode | ToolNode>,
): DomainNode {
  return { kind: "domain", key, children };
}

/** L2: Navigable parent with children (클릭 가능 + children 토글) */
export function group(
  label: string,
  to: string,
  desc: string,
  Icon: LucideIcon,
  opts: BaseOpts,
  children: LeafNode[],
): GroupNode {
  return { kind: "group", label, desc, to, Icon, opts, children };
}

/** L2: Standalone leaf at group level (단일 페이지, children 없음) */
export function tool(
  label: string,
  to: string,
  desc: string,
  Icon: LucideIcon,
  opts?: BaseOpts,
): ToolNode {
  return { kind: "tool", label, desc, to, Icon, opts: opts ?? {} };
}

/** L3: Child entry under a group() */
export function leaf(
  navLabel: string,
  to: string,
  desc: string,
  Icon: LucideIcon,
  opts?: BaseOpts,
): LeafNode {
  return {
    kind: "leaf",
    navLabel,
    label: navLabel,
    desc,
    to,
    Icon,
    opts: opts ?? {},
  };
}
